<template>
  <v-card>
    <v-card-title>
      {{ editMode ? $t('guarantees.edit') : $t('guarantees.add') }}
    </v-card-title>
    <v-card-text>
      <v-container>
        <v-row>
          <v-col>
            <GuaranteesPartsSelect v-model="part" :parts="partsList" :disabled="partsList.length < 2" :isMultiCurrency="contract.isMultiCurrency"/>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-combobox
              v-model="form.name"
              :items="nameList"
              item-text="name"
              item-value="name"
              :label="$t('guarantees.name')"
              counter="128"
              dense
              outlined
              @change="$v.form.name.$touch()"
              :error-messages="errorMessages('name')"
              @keyup.enter.prevent="handleAddNewName"
            >
              <template v-slot:no-data>
                <v-row class="ma-1">
                  <v-col><span v-html="$t('guarantees.noResults')"></span></v-col>
                </v-row>
              </template>
            </v-combobox>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              v-model="form.beneficiaryName"
              :label="$t('guarantees.beneficiaryName')"
              counter="128"
              dense
              outlined
              @change="$v.form.beneficiaryName.$touch()"
              :error-messages="errorMessages('beneficiaryName')"
            />
          </v-col>
          <v-col>
            <v-text-field
              v-model="form.obligorName"
              :label="$t('guarantees.obligorName')"
              counter="128"
              dense
              outlined
              @change="$v.form.obligorName.$touch()"
              :error-messages="errorMessages('obligorName')"
            />
          </v-col>
        </v-row>
        <v-row class="ma-1" align="center">
          <span class="mr-2 text-overline">{{ $t('guarantees.guarantor') }}</span>
          <v-divider />
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              v-model="form.guarantorName"
              :label="$t('guarantees.guarantorName')"
              counter="128"
              dense
              outlined
              @change="$v.form.guarantorName.$touch()"
              :error-messages="errorMessages('guarantorName')"
            />
          </v-col>
          <v-col>
            <v-text-field
              v-model="form.guarantorRating"
              :label="$t('guarantees.guarantorRating')"
              counter="128"
              dense
              outlined
              @change="$v.form.guarantorRating.$touch()"
              :error-messages="errorMessages('guarantorRating')"
            />
          </v-col>
          <v-col>
            <v-text-field
              v-model="form.guarantorContractRating"
              :label="$t('guarantees.guarantorContractRating')"
              counter="128"
              dense
              outlined
              @change="$v.form.guarantorContractRating.$touch()"
              :error-messages="errorMessages('guarantorContractRating')"
            />
          </v-col>
        </v-row>
        <v-row class="ma-1" align="center"><v-divider /></v-row>
        <v-row>
          <v-col v-if="!isEmployerGuarantee">
            <v-text-field
              v-model="form.no"
              :label="$t('guarantees.number')"
              dense
              outlined
              counter="128"
              @change="$v.form.no.$touch()"
              :error-messages="errorMessages('number')"
            />
          </v-col>
          <v-col>
            <v-select
              v-model="form.status"
              :items="guaranteesStatuses"
              :label="$t('guarantees.status')"
              dense
              outlined
              @change="$v.form.status.$touch()"
              :error-messages="errorMessages('status')"
            />
          </v-col>
          <v-col>
            <vf-date-input
              v-model="form.startDate"
              :label="$t('guarantees.startDate')"
              dense
              outlined
              @change="$v.form.startDate.$touch()"
              :error-messages="errorMessages('startDate')"
            />
          </v-col>
          <v-col>
            <vf-date-input
              v-model="form.expiryDate"
              :label="$t('guarantees.expiryDate')"
              dense
              outlined
              @change="$v.form.expiryDate.$touch()"
              :error-messages="errorMessages('expiryDate')"
              :disabled="editMode && haveDocument"
            />
          </v-col>
        </v-row>
        <v-row>
          <template>
            <v-col>
              <VfPercentageInput
                v-model="form.securedAmountPercent"
                :label="$t(partTranslationKey + '.percent')"
                dense
                outlined
                @change="$v.form.securedAmountPercent.$touch()"
                :outputValue.sync="percentOutputCents"
                :inputValue="signedPriceCents"
                :error-messages="errorMessages('securedAmountPercent')"
                :suffix="percentSuffix"
              />
            </v-col>
            <v-col>
              <VfCurrencyInput
                v-model="form.securedAmountCents"
                dense
                outlined
                :currency="currentCurrency"
                @change="$v.form.securedAmountCents.$touch()"
                :error-messages="errorMessages('securedAmountCents')"
                :label="$t('guarantees.securedAmount')"
              />
            </v-col>
          </template>
        </v-row>
        <v-row class="ma-1" align="center">
          <span class="mr-2 text-overline">{{ $t('guarantees.internal') }}</span>
          <v-divider />
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              v-model="form.documentUrl"
              :label="$t('guarantees.documentUrl')"
              dense
              outlined
              @change="$v.form.documentUrl.$touch()"
              :error-messages="errorMessages('documentUrl')"
              :disabled="editMode && haveDocument"
            />
          </v-col>
          <v-col>
            <v-autocomplete
              v-model="form.internalContact"
              :label="$t('guarantees.internalContact')"
              placeholder="Start typing to search"
              outlined
              :items="graph.internalContact.items"
              :loading="graph.internalContact.isLoading"
              :search-input.sync="search.internalContact"
              hide-no-data
              dense
              item-text="displayName"
              item-value="mailNickname"
              @input="$v.form.internalContact.$touch()"
              :error-messages="errorMessages('internalContact')"
            />
          </v-col>
          <v-col v-if="isEmployerGuarantee" cols="12">
            <v-textarea
              v-model="form.physicalStorage"
              :label="$t('guarantees.physicalStorage')"
              dense
              outlined
              rows="3"
              counter="256"
              @change="$v.form.physicalStorage.$touch()"
              :error-messages="errorMessages('physicalStorage')"
            />
          </v-col>
        </v-row>
        <v-row class="ma-1" align="center" v-if="!isEmployerGuarantee">
          <span class="mr-2 text-overline">{{ $t('guarantees.return') }}</span>
          <v-divider />
        </v-row>
        <v-row v-if="!isEmployerGuarantee">
          <v-col>
            <v-text-field
              v-model="form.sentBack"
              :label="$t('guarantees.sentBack')"
              dense
              outlined
              counter="128"
              @change="$v.form.sentBack.$touch()"
              :error-messages="errorMessages('sendBack')"
            />
          </v-col>
          <v-col>
            <v-text-field
              v-model="form.externalContact"
              :label="$t('guarantees.externalContact')"
              dense
              outlined
              counter="128"
              @change="$v.form.externalContact.$touch()"
              :error-messages="errorMessages('externalContact')"
            />
          </v-col>
          <v-col>
            <v-text-field
              v-model="form.returnAddress"
              :label="$t('guarantees.returnAddress')"
              dense
              outlined
              counter="256"
              @change="$v.form.returnAddress.$touch()"
              :error-messages="errorMessages('returnAddress')"
            />
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
    <v-card-actions>
      <v-btn
        v-if="editMode && haveDocument"
        small
        rounded
        color="primary"
        @click="handleProlongateGuarantee"
      >
        {{ $t('guarantees.prolongation') }}
      </v-btn>
      <v-spacer />
      <v-btn
        rounded
        dark
        dense
        outlined
        color="primary"
        @click="cancel"
      >
        {{ $t('common.cancel') }}
      </v-btn>
      <SubmitButton @click="submit" />
    </v-card-actions>
  </v-card>
</template>

<script>
import { errorMessageGenerator, guaranteeModel } from '@/validators'
import { DEFAULT_GUARANTEE, DEFAULT_CONTRACT, SITES } from '@/helpers'
import { mapState } from 'vuex'
import FormFieldGraphSearch from '@/mixins/FormFieldGraphSearch'
import { createOrUpdateGuarantee, saveDictionaryItem, getAllDictionaries } from '@/services'
import { getLocalizationContext } from '../../helpers/translations'

export default
{
  name: 'GuaranteeForm',
  props: {
    value: {
      type: Object,
      default: () => ({ ...DEFAULT_GUARANTEE })
    },
    contract: {
      type: Object,
      default: () => ({ ...DEFAULT_CONTRACT })
    },
    site: String
  },
  mixins: [FormFieldGraphSearch],
  data () {
    return {
      graphBasedFields: ['internalContact'],
      originalDateMenu: false,
      forecastedDateMenu: false,
      expectedInvoiceDateMenu: false,
      expectedPaymentDateMenu: false,
      form: this.prepareGuarantee(),
      part: null,
      percentOutputCents: 0
    }
  },
  mounted () {
    this.part = this.getInitialPart()
  },
  created () {
    this.$eventBus.$on('CLICK_OUTSIDE_MODAL', () => {
      this.cancel()
    })
    this.form.securedAmountPercent = this.calculatePercentValue(this.value.securedAmountCents)
  },
  computed: {
    ...mapState({
      nameList: state => state.guaranteesNames,
      guaranteesStatuses: state => state.statuses
        .filter(i => i.show.includes('guarantee'))
        .map((i) => ({ text: i.name, value: i.code }))
    }),
    editMode () {
      return !!this.value.id
    },
    haveDocument () {
      return this.value.documents && !!this.value.documents.length
    },
    percentSuffix () {
      return this.$t('guarantees.percentSuffix', { amount: this.$options.filters.formatCurrency(this.signedPriceCents, { key: this.currentCurrency.key }) })
    },
    isEmployerGuarantee () {
      return this.site === SITES.employer
    },
    signedPriceCents () {
      return this.part ? this.part.signedPriceCents : 0
    },
    partsList () {
      const parts = this.contract.parts.map(part => {
        return {
          ...part,
          formattedPrice: part.signedPriceCents
            ? this.$options.filters.formatCurrency(part.signedPriceCents, { key: part.currency, value: 1 })
            : null
        }
      })
      if (!this.contract.isMultiCurrency && this.contract.parts.length > 1) {
        const signedPriceCents = this.contract.parts.reduce((acc, current) => acc + current.signedPriceCents, 0)
        const allSitesPart = { id: null, formattedPrice: null, signedPriceCents, name: this.$t('site.all') }
        return [allSitesPart, ...parts]
      }
      return parts
    },
    currentCurrency () {
      const currency = this.part && this.part.id ? this.contract.currencies.find(currency => currency.key === this.part.currency) : this.contract.currencies[0]
      return { ...currency, value: 1 }
    },
    partTranslationKey () {
      return getLocalizationContext(this.contract.isMultiCurrency)
    }
  },
  methods: {
    getInitialBeneficiaryName () {
      if (this.value.beneficiaryName) {
        return this.value.beneficiaryName
      }
      return this.site === SITES.employer ? this.contract.parts.map(part => part.contractor.companyName)[0] : this.contract.employer.companyName
    },
    getInitialObligorName () {
      if (this.value.obligorName) {
        return this.value.obligorName
      }
      return this.site !== SITES.employer ? this.contract.parts.map(part => part.contractor.companyName)[0] : this.contract.employer.companyName
    },
    getInitialPart () {
      return this.value.partId ? this.contract.parts.find(part => part.id === this.value.partId) : this.partsList[0]
    },
    handleProlongateGuarantee () {
      this.$eventBus.$emit('OPEN_MODAL', {
        component: 'ProlongationForm',
        options: { maxWidth: 650 },
        scope: {
          guarantee: this.value
        },
        handlers: {
          update: () => {
            this.$emit('update')
            this.$eventBus.$emit('CLOSE_MODAL')
          }
        }
      })
    },
    async handleAddNewName () {
      await saveDictionaryItem('guarantees-names', { name: this.form.name })
      await getAllDictionaries()
    },
    prepareGuarantee () {
      return {
        ...DEFAULT_GUARANTEE,
        site: this.site,
        ...this.value,
        documentUrl: this.value.document ? this.value.document.url || '' : '',
        beneficiaryName: this.getInitialBeneficiaryName(),
        obligorName: this.getInitialObligorName()
      }
    },
    errorMessages (key) {
      return errorMessageGenerator(this.$v.form[key], key)
    },
    cancel () {
      this.$v.$reset()
      this.$eventBus.$emit('CLOSE_MODAL')
      this.form = this.prepareGuarantee()
    },
    async submit () {
      this.$v.form.$touch()
      if (!this.$v.form.$invalid) {
        await createOrUpdateGuarantee({
          ...this.form,
          name: this.form.name.name || this.form.name,
          internalContact: this.form.internalContact.mailNickname || this.form.internalContact || '',
          partId: this.part.id
        }, this.contract.id)
        this.$eventBus.$emit('CLOSE_MODAL')
        this.$emit('update')
        this.form = this.prepareGuarantee()
      }
    },
    calculateCentsValue (value) {
      return Math.round((value) * this.signedPriceCents)
    },
    calculatePercentValue (value) {
      return value / this.signedPriceCents
    }
  },
  watch: {
    part (v) {
      if (v.id) {
        this.form.beneficiaryName = this.site === SITES.employer ? v.contractor.companyName : this.contract.employer.companyName
        this.form.obligorName = this.site !== SITES.employer ? v.contractor.companyName : this.contract.employer.companyName
      }
      this.form.securedAmountPercent = this.calculatePercentValue(this.form.securedAmountCents)
    },
    percentOutputCents (v) {
      if (v !== this.form.securedAmountCents) {
        this.form.securedAmountCents = v
      }
    },
    'form.securedAmountCents' (v) {
      if (v !== this.percentOutputCents) {
        const percent = this.calculatePercentValue(v)
        this.form.securedAmountPercent = percent
      }
    }
  },
  validations: guaranteeModel
}
</script>
